import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Mail, ChevronDown, ChevronLeft, ChevronRight, Award, Shield, Building2 } from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/wocjce1q_Logo-Lackcenter-NEU.png";
const PHONE_NUMBER = "tel:+493916357115";
const EMAIL = "mailto:info@lackzentrum-wilke.com";

// Hero Slider Images
const heroSlides = [
  {
    image: "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/afpfrzcu_Schwarz_bearbeitet.jpg",
    alt: "Professionelle Fahrzeuglackierung"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/i35efism_Autolackierer_bearbeitet.jpg",
    alt: "Lackierarbeiten in der Werkstatt"
  },
  {
    image: "https://images.pexels.com/photos/8470881/pexels-photo-8470881.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop",
    alt: "Lackierer sprüht Lack auf Fahrzeug"
  }
];

// Trust Elements
const trustElements = [
  { icon: Award, text: "Seit 1988" },
  { icon: Shield, text: "Zertifizierter Fachbetrieb" },
  { icon: Building2, text: "Für alle Versicherungen" }
];

export const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const parallaxRef = useRef(null);

  // Auto-slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Parallax effect
  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrollY = window.scrollY;
        parallaxRef.current.style.transform = `translateY(${scrollY * 0.3}px)`;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector("#kontakt");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Slider with Parallax */}
      <div className="absolute inset-0 z-0">
        <div ref={parallaxRef} className="absolute inset-0 will-change-transform">
          {heroSlides.map((slide, index) => (
            <img
              key={index}
              src={slide.image}
              alt={slide.alt}
              className={`absolute inset-0 w-full h-[120%] object-cover transition-opacity duration-1000 ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
        </div>
        {/* Darker Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/50" />
      </div>

      {/* Slider Controls */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
        aria-label="Vorheriges Bild"
      >
        <ChevronLeft className="h-6 w-6 text-white" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
        aria-label="Nächstes Bild"
      >
        <ChevronRight className="h-6 w-6 text-white" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSlide ? "bg-primary w-8" : "bg-white/50 hover:bg-white/70"
            }`}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 lg:px-8 pt-24 pb-16">
        <div className="max-w-3xl">
          {/* Trust Elements - Directly Visible */}
          <div className="flex flex-wrap gap-4 mb-8 animate-fade-in">
            {trustElements.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2"
              >
                <item.icon className="h-4 w-4 text-primary-light" />
                <span className="text-sm font-medium text-white">
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          {/* Main Heading */}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
            Ihr Lack- und Karosseriezentrum in Magdeburg
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-10 max-w-2xl animate-fade-in" style={{ animationDelay: "0.2s" }}>
            Professionelle Fahrzeuglackierung und Unfallinstandsetzung aus einer Hand. 
            Digitale Farbtonmessung, modernste Technik und über 35 Jahre Erfahrung.
          </p>

          {/* CTA Buttons - One Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <Button
              variant="hero"
              size="xl"
              onClick={scrollToContact}
              className="gap-2 text-lg px-8 py-6 shadow-2xl hover:scale-105 transition-transform"
            >
              <Mail className="h-5 w-5" />
              Kostenvoranschlag anfragen
            </Button>
            <Button
              variant="hero-secondary"
              size="xl"
              asChild
              className="gap-2"
            >
              <a href={PHONE_NUMBER}>
                <Phone className="h-5 w-5" />
                0391 635 771 15
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-fade-in" style={{ animationDelay: "0.5s" }}>
        <button
          onClick={(e) => {
            e.preventDefault();
            document.querySelector("#leistungen")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors"
          aria-label="Zu den Leistungen scrollen"
        >
          <span className="text-sm font-medium">Mehr entdecken</span>
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </button>
      </div>
    </section>
  );
};

export default Hero;
